
import { useState } from 'react';
import { format, addDays, addWeeks } from 'date-fns';
import { Calendar, Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { TimeSlot, SpecificDateAvailability } from '@/components/therapists/calendar/types';

export type SubscriptionDetails = {
  therapistId: string;
  therapistName: string;
  frequency: 'weekly' | 'biweekly';
  startDate: Date;
  timeSlot: string;
};

export type SubscriptionBookingProps = {
  therapistId: string;
  therapistName: string;
  sessionPrice: number;
  weeklyAvailability: TimeSlot[];
  specificDates: SpecificDateAvailability[];
  existingAppointments: Date[];
  currentBalance: number;
  onSubscribe: (details: SubscriptionDetails) => void;
  onAddFunds: () => void;
};

const SubscriptionBooking = ({
  therapistId,
  therapistName,
  sessionPrice,
  weeklyAvailability,
  specificDates,
  existingAppointments,
  currentBalance,
  onSubscribe,
  onAddFunds
}: SubscriptionBookingProps) => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [frequency, setFrequency] = useState<'weekly' | 'biweekly'>('weekly');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [timeSlot, setTimeSlot] = useState<string | null>(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  
  // Calculate subscription cost
  const subscriptionCost = frequency === 'weekly' ? sessionPrice * 4 : sessionPrice * 2;
  
  const handleStartDateSelect = (selected: Date | undefined) => {
    setStartDate(selected);
    setTimeSlot(null);
    
    if (selected) {
      // In a real implementation, this would fetch available time slots from the backend
      // For now, we'll use mock data
      const dayOfWeek = selected.getDay();
      
      // Check if there's specific availability for this date
      const specificDate = specificDates.find(sd => 
        new Date(sd.date).toDateString() === selected.toDateString()
      );
      
      if (specificDate) {
        setAvailableTimeSlots(specificDate.slots.map(slot => slot.start));
      } else {
        // Otherwise use weekly availability
        const slotsForDay = weeklyAvailability
          .filter(slot => slot.day === dayOfWeek)
          .map(slot => slot.start);
        
        setAvailableTimeSlots(slotsForDay);
      }
    }
  };
  
  const getFutureDates = () => {
    if (!startDate || !timeSlot) return [];
    
    const dates = [];
    const interval = frequency === 'weekly' ? 1 : 2;
    
    for (let i = 0; i < 4; i++) {
      dates.push(addWeeks(startDate, interval * i));
    }
    
    return dates;
  };
  
  const handleSubscribe = () => {
    if (!startDate || !timeSlot) {
      toast({
        title: "Missing information",
        description: "Please select a start date and time slot",
        variant: "destructive"
      });
      return;
    }
    
    if (currentBalance < subscriptionCost) {
      toast({
        title: "Insufficient funds",
        description: "Please add more funds to your account",
        variant: "destructive"
      });
      return;
    }
    
    const subscriptionDetails: SubscriptionDetails = {
      therapistId,
      therapistName,
      frequency,
      startDate: new Date(startDate),
      timeSlot
    };
    
    onSubscribe(subscriptionDetails);
    setIsOpen(false);
    
    toast({
      title: "Subscription confirmed",
      description: `Your ${frequency} sessions with ${therapistName} have been scheduled`
    });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full">
          Subscribe to Regular Sessions
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="sticky top-0 bg-white z-10 pb-2">
          <DialogTitle>Subscribe to Regular Sessions</DialogTitle>
          <DialogDescription>
            Schedule recurring sessions with {therapistName}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div>
            <h3 className="font-medium mb-2">Session Frequency</h3>
            <RadioGroup 
              value={frequency} 
              onValueChange={(value) => setFrequency(value as 'weekly' | 'biweekly')}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="weekly" id="weekly" />
                <Label htmlFor="weekly">Weekly</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="biweekly" id="biweekly" />
                <Label htmlFor="biweekly">Bi-weekly</Label>
              </div>
            </RadioGroup>
          </div>
          
          <div>
            <h3 className="font-medium mb-2 flex items-center">
              <Calendar className="h-4 w-4 mr-1" /> 
              Select Start Date
            </h3>
            <CalendarComponent
              mode="single"
              selected={startDate}
              onSelect={handleStartDateSelect}
              className="border rounded-md pointer-events-auto"
              disabled={(date) => {
                const now = new Date();
                now.setHours(0, 0, 0, 0);
                return date < addDays(now, 1);
              }}
            />
          </div>
          
          {startDate && (
            <div>
              <h3 className="font-medium mb-2">Select Time Slot</h3>
              {availableTimeSlots.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {availableTimeSlots.map((time) => (
                    <Button
                      key={time}
                      variant={timeSlot === time ? 'default' : 'outline'}
                      className="text-sm"
                      onClick={() => setTimeSlot(time)}
                    >
                      {time}
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No available time slots for this date
                </p>
              )}
            </div>
          )}
          
          {startDate && timeSlot && (
            <div className="mt-2">
              <h3 className="font-medium mb-2">Your Schedule</h3>
              <div className="border rounded-md p-3 bg-muted/30">
                <p className="text-sm font-medium mb-2">
                  {frequency === 'weekly' ? 'Weekly' : 'Bi-weekly'} sessions at {timeSlot}
                </p>
                <ul className="space-y-2">
                  {getFutureDates().map((date, index) => (
                    <li key={index} className="text-sm flex items-center">
                      <Check className="h-3 w-3 text-green-500 mr-2" />
                      {format(date, 'EEE, MMM d, yyyy')}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          <div className="bg-muted rounded-md p-3 mt-2">
            <div className="flex justify-between items-center">
              <span>Price per session:</span>
              <span>${sessionPrice.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center font-medium mt-1">
              <span>Total subscription cost:</span>
              <span>${subscriptionCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <span>Your Balance:</span>
              <span>${currentBalance.toFixed(2)}</span>
            </div>
            
            {currentBalance < subscriptionCost && (
              <div className="mt-2 text-sm text-red-600 flex flex-col space-y-2">
                <p>Your balance is insufficient. Please add funds to your account.</p>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  onClick={onAddFunds}
                  className="w-full"
                >
                  Add Funds
                </Button>
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter className="sticky bottom-0 pb-0 pt-2 bg-white z-10 mt-2">
          <Button 
            onClick={handleSubscribe}
            disabled={!startDate || !timeSlot || currentBalance < subscriptionCost}
            className="w-full flex items-center justify-center"
          >
            Confirm Subscription <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionBooking;
